import audioPlayer from "~/resources/audio-player";
import inlineAudioPlayer from "~/resources/inline-audio-player";

interface WysiwygState {
   expand: { selector: string; els: HTMLElement[] };
   audio: { selector: string; players: AudioPlayerInstance[] };
   inlineAudio: { selector: string; players: InlineAudioPlayerInstance[] };
   wistia: { selector: string };
   toc: unknown[];
}

// Resources/* legacy classes — narrow to the methods we call.
interface AudioPlayerInstance {
   destroy(): void;
}
interface InlineAudioPlayerInstance {
   destroy(): void;
}

declare global {
   interface Window {
      _wq?: unknown[];
   }
}
declare const _wq: unknown[];

export function useWYSIWYG(
   initialState: WysiwygState = {
      expand: { selector: ".block-show-hide", els: [] },
      audio: { selector: ".audio-player", players: [] },
      inlineAudio: { selector: ".inline-audio-player", players: [] },
      wistia: { selector: ".wistia_embed" },
      toc: [],
   },
) {
   // STATE =============================
   const data = reactive(initialState);

   // WATCHERS =============================
   const audioWatcher = async (el: HTMLElement | null) => {
      await nextTick();
      if (!el) return;
      const targetEls = Array.from(
         el.querySelectorAll<HTMLElement>(data.audio.selector),
      );
      targetEls?.length &&
         targetEls.forEach((target) => {
            const url = target.querySelector<HTMLAudioElement>("audio")?.src ?? "";
            if (!url) return;
            const id = target.id;
            const newPlayer = new audioPlayer(url, id, target);
            data.audio.players.push(newPlayer);
         });
   };

   const inlineAudioWatcher = async (el: HTMLElement | null) => {
      if (!el) return;
      await nextTick();
      const targetEls = Array.from(
         el.querySelectorAll<HTMLElement>(data.inlineAudio.selector),
      );
      targetEls?.length &&
         targetEls.forEach((target) => {
            const inlinePlayer = new inlineAudioPlayer(target);
            data.inlineAudio.players.push(inlinePlayer);
         });
   };

   const expandWatcher = (el: HTMLElement) => {
      const expandableEls = Array.from(
         el.querySelectorAll<HTMLElement>(data.expand.selector),
      );
      const handleExpandBox = (tabEl: HTMLElement, trayEl: HTMLElement) => {
         const openTray = () => {
            tabEl.innerText = "Hide Details";
            trayEl.style.maxHeight = `${trayEl.scrollHeight}px`;
         };
         const closeTray = () => {
            tabEl.innerText = "Show Details";
            trayEl.style.maxHeight = `0`;
         };
         trayEl.classList.toggle("open");
         trayEl.classList.contains("open") ? openTray() : closeTray();
      };
      expandableEls?.length &&
         expandableEls.forEach((expandEl) => {
            data.expand.els.push(expandEl);
            const tabEl = expandEl.querySelector<HTMLElement>("h4");
            const trayEl = expandEl.querySelector<HTMLElement>("div.expand-box");
            if (tabEl && trayEl && !tabEl.dataset.hasListener) {
               tabEl.addEventListener("click", () =>
                  handleExpandBox(tabEl, trayEl),
               );
               tabEl.dataset.hasListener = "true";
            }
         });
   };

   const wistiaWatcher = (el: HTMLElement) => {
      const { $posthog } = useNuxtApp();
      const wistiaEls = Array.from(
         el.querySelectorAll<HTMLElement>(data.wistia.selector),
      );
      wistiaEls?.length &&
         wistiaEls.forEach((target) => {
            window._wq = window._wq || [];
            _wq.push({
               id: target.dataset.wistiaHashed,
               options: {
                  copyLinkAndThumbnailEnabled: false,
                  playbackRateControl: false,
                  controlsVisibleOnLoad: false,
                  playerColor: "#2C2C30",
                  videoFoam: true,
               },
               onReady(video: WistiaVideo) {
                  video.bind("play", () => {
                     $posthog().capture("video_played", {
                        video_title: video?._originalMediaData?.name,
                     });
                  });
                  video.bind("pause", () => {});
                  video.bind("end", () => {
                     $posthog().capture("video_completed", {
                        video_title: video?._originalMediaData?.name,
                     });
                  });
               },
            });
         });
   };

   // DESTROY =============================
   onBeforeUnmount(() => {
      data.audio.players?.length &&
         data.audio.players.forEach((player) => {
            player.destroy();
         });
      data.audio.players = [];
      data.inlineAudio.players?.length &&
         data.inlineAudio.players.forEach((player) => {
            player.destroy();
         });
      data.inlineAudio.players = [];
      data.expand.els?.length &&
         data.expand.els.forEach((expandEl) => {
            expandEl.removeEventListener("click", () => {});
         });
      data.expand.els = [];
   });

   // INIT =============================
   const initWatchers = (el: HTMLElement) => {
      audioWatcher(el);
      inlineAudioWatcher(el);
      expandWatcher(el);
      wistiaWatcher(el);
   };

   return {
      initWatchers,
   };
}

// Wistia's onReady payload — only the methods we use are typed.
interface WistiaVideo {
   bind(event: "play" | "pause" | "end" | "heightchange", cb: () => void): void;
   _originalMediaData?: { name?: string };
}
